import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {httpClientV1} from "../../infrastructure/http";
import {
  createLiveEventRepository,
  type LiveEventListItem,
} from "../../features/live-events";
import {AnalyticsEvent} from "../../utils/analytics";
import {message} from "antd";
import {TProfileApiResponse} from "../../redux/profile/i-profile-1";
import {CurrencyResponse} from "../../redux/service/i-service";
import {Currency} from "../../components/calls/service/service-form/i-service-form";
import {CoverImagesItemResponse} from "../products/types";

// Re-export v2 hooks for gradual migration (strangler pattern)
// These hooks use the new PDD architecture.
// Components can import from here during migration, then switch to
// importing directly from "../../features/live-events" once stable.
export {
  useLiveEvent,
  useLiveEvents,
  useCreateLiveEvent,
  useUpdateLiveEvent,
  useDeleteLiveEvent,
  useJoinLiveEvent,
  useRescheduleLiveEvent,
  sortLiveEventsByTime,
  type LiveEvent,
  type LiveEventFilter,
  type PaginatedLiveEvents,
  type CreateLiveEventRequest,
  type UpdateLiveEventRequest,
  type JoinLiveEventResult,
  type RescheduleLiveEventRequest,
} from "../../features/live-events";

export type TLiveClassApiResponse = {
  activeSession: {
    name: string;
    startDate: string;
    startTime: string;
    duration: number;
    endTime: string;
  };
  activeSessionId: number;
  attendees: number;
  bookingQuestions: {
    question: string;
    required: boolean;
  }[];
  callPrice: number;
  callPriceCurrency: string;
  cover_image: string;
  cover_images?: CoverImagesItemResponse[];
  createdAt: Date;
  currencies: CurrencyResponse[];
  description: string;
  hide_from_menu: boolean;
  id: number;
  name: string;
  reference: string;
  roomId: string;
  tiny_url: string;
  session_count: number;
  session_info: {
    name: string;
    startDate: string;
    startTime: string;
    duration: number;
    endTime: string;
  }[];
  slug: string;
  statusId: number;
  statusName: "Paused" | "Active" | "In Progress";
  topic_type: "single" | "series";
  request_phone_number: boolean;
  // topics: string[];
  updatedAt: Date;
  url_redirect?: string;
  userId: number;
  is_custom_room: boolean;
  video_platform: string;
  enable_currency_conversion?: boolean;
  hasGoogleMeet?: boolean;
};

export type GetLiveClassApiResponse = TLiveClassApiResponse & {
  bookedCounts: number;
};

export type TCreateLiveClassApiPayload = Omit<
  TLiveClassApiResponse,
  | "id"
  | "userId"
  | "slug"
  | "activeSession"
  | "roomId"
  | "activeSessionId"
  | "session_count"
  | "statusId"
  | "reference"
  | "createdAt"
  | "updatedAt"
  | "bookingQuestions"
  | "session_info"
  | "cover_image"
  | "hide_from_menu"
  | "statusName"
  | "currencies"
  | "tiny_url"
  | "is_custom_room"
  | "video_platform"
  | "cover_images"
  | "callPrice"
  | "callPriceCurrency"
> & {
  session_info: {
    name: string;
    startDate: string;
    startTime: string;
    duration: number | string;
  }[];
  coverImage: string;
  has_custom_link: boolean;
  custom_link: string;
  hideFromMenu: boolean;
  isPauseable: boolean;
  currencies: Currency[];
  enable_currency_conversion: boolean;
  selected_currencies: string[];
} & Partial<{
    bookingQuestions: {
      question: string;
      required: boolean;
    }[];
    // co_hosts: {
    //   host_id: number;
    //   email_address: string;
    //   first_name: string;
    //   last_name: string;
    // }[];
  }>;

export type PublicProfileLiveClassApiResponseEventCohost = {
  id: number;
  userId: number;
  username: string;
  description: string;
  buffer_time: number;
  shortBio: string;
  twitter: string | null;
  facebook: string | null;
  instagram: string | null;
  tiktok: string | null;
  discord: string | null;
  youtube: string | null;
  dribble: string | null;
  behance: string | null;
  twitch: string | null;
  github: string | null;
  website: string | null;
  linkedin: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    onboarding_reminder: number;
    country: string;
    password: null;
    statusId: number;
    isSocialAuth: boolean;
    avatar: string | null;
    cover_image: string | null;
    createdAt: string;
    updatedAt: string;
    displayName: string | null;
  };
};
export type PublicProfileLiveClassApiResponseEvent = TLiveClassApiResponse & {
  co_hosts: PublicProfileLiveClassApiResponseEventCohost[];
};
export type PublicProfileApiResponseEvent = TLiveClassApiResponse & {
  is_co_host: boolean;
  hostProfile?: PublicProfileLiveClassApiResponseEventCohost;
};
export type TPublicProfileLiveClassApiResponse = TProfileApiResponse & {
  event: PublicProfileLiveClassApiResponseEvent;
  totalSessions: number;
  bookedCounts: number;
  spotLeft: number;
  // statusName: "Paused" | "Active" | "In Progress";
  activeSession: {
    name: string;
    startDate: string;
    startTime: string;
    duration: number;
    endTime: string;
  };
  timeZone: {
    id: number;
    userId: number;
    coachli_timezone: string;
    createdAt: Date;
    updatedAt: Date;
  };
};

export type TLiveClassBookingPayload = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  coach_timezone: string;
  customer_timezone: string;
  phone_number?: string;
  eventId: number;
  amount: number;
  currency: string;
  slug: string;
  reference: string | null;
};

export type TPremiumLiveClassBookingPayload = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  coach_timezone: string;
  customer_timezone: string;
  eventId: number;
  amount: number;
  reference: string;
  currency: string;
};

export type TLiveClassBookingApiResponse = {
  userId: number;
  eventId: number;
  paymentProvider: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  reference: string;
  statusId: number;
  amount: number;
  isStripe: boolean;
  redirectUrl?: string;
  currency: string;
  isPaid: boolean;
  username: string;
  displayName: string | null;
};
export type TPremiumLiveClassBookingApiResponse = {
  userId: number;
  eventId: number;
  paymentProvider: string;
  emailAddress: string;
  reference: string;
  statusId: number;
  amount: number;
  isStripe: boolean;
  redirectUrl?: string;
};

export type TAttendee = {
  coach_end_time: string;
  coach_event_time: string;
  createdAt: string;
  current_session: number;
  customer_end_time: string;
  customer_event_time: string;
  emailAddress: string;
  eventId: number;
  event_date: string;
  firstName: string;
  id: number;
  is_paid: boolean;
  is_series: boolean;
  lastName: string;
  liveEvent: TLiveClassApiResponse;
  paymentProvider: string;
  reference: string;
  roomId: string;
  statusId: number;
  updatedAt: Date;
  userId: number;
};
export type TLiveClassAttendeesApiResponse = {
  totalRevenue: {currency: string; revenue: number}[];
  totalAttendees: number;
  attendees: TAttendee[];
  event: TLiveClassApiResponse;
};

export type TLiveClassAttendeeDetailsApiResponse = {
  booking: {
    coach_end_time: string;
    coach_event_time: string;
    createdAt: string;
    current_session: number;
    customer_end_time: string;
    customer_event_time: string;
    emailAddress: string;
    eventId: number;
    event_date: string;
    firstName: string;
    id: number;
    is_paid: boolean;
    is_series: boolean;
    lastName: string;
    paymentProvider: string;
    phone_number: string;
    reference: string;
    roomId: string;
    statusId: number;
    updatedAt: string;
    userId: number;
  };
  event: TLiveClassApiResponse;
  bookingQuestions: {answer?: string | null; question: string}[] | null;
  totalPurchases: number;
  totalVolume: number;
};

export type TJoinLiveClassApiResponse = {
  event: TLiveClassApiResponse;
  booking: {
    bookingToken: string;
  };
  bookingToken: string;
  videoPlatform: "dailyCo" | "videoSdk";
  dailyCoUrl: string;
};

/**
 * v2 caps `perPage` at 100 (`PaginationDto`). v1 `live-event/upcoming` was
 * unpaginated and every consumer wants "all upcoming events", so this asks for
 * the maximum page size — same approach as the sibling offering reads
 * (`useSessions({page: 1, perPage: 100})`, `useDigitalProducts(1, 100)`).
 */
const UPCOMING_LIVE_CLASSES_PER_PAGE = 100;

/**
 * Upcoming live classes for the coach dashboard — v2 `GET live-event/upcoming`,
 * delegated to `features/live-events` (`LiveEventRepositoryV2.getList`).
 *
 * Response-shape handling lives in the feature mapper: v2 wraps the payload as
 * `{status, message, statusCode, data: {data, total, hasNextPage, hasPrevPage}}`
 * and returns the slim `UpcomingLiveEventListItemResponseDto`
 * (`amount`/`currency`, not `callPrice`/`callPriceCurrency`), which
 * `LiveEventMapperV2.toDomainUpcomingList` unwraps and renames. This hook then
 * keeps handing consumers a bare array, as v1 did.
 *
 * The element type narrows from `TLiveClassApiResponse` to the domain
 * `LiveEventListItem`, which still carries everything the two call sites read
 * (`id`, `name`, `callPrice`, plus `length`) — v2 has no fat list payload to
 * map from. Query key, refetch behavior and the array return shape are
 * unchanged, so `useDeleteLiveClass`'s optimistic update and
 * `useSaveLiveClassAsDraft`'s invalidation still line up.
 */
const useGetLiveClasses = () => {
  const repository = createLiveEventRepository();

  return useQuery<LiveEventListItem[], AxiosError<{status: boolean; message: string}>>({
    queryKey: ["live_classes"],
    queryFn: () =>
      repository
        .getList("upcoming", {page: 1, perPage: UPCOMING_LIVE_CLASSES_PER_PAGE})
        .then((result) => result.data),
    refetchInterval: 300000,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};
/**
 * Permanent, documented exception to the v1 -> v2 migration: `LiveEventRepositoryV2`
 * has no draft concept at all, so there is no v2 endpoint to list draft live
 * classes. Calls v1 directly via `httpClientV1` (same pattern as
 * `MediaRepositoryV1.uploadContent` and `StripeRepository.loginToStripe`)
 * instead of the generic feature-flag-driven `useClient`. Do not migrate this to `useClientV2` — ask
 * before changing.
 */
const useGetLiveClassesDraft = () => {
  return useQuery<
    TLiveClassApiResponse[],
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["live_classes_draft"],
    queryFn: () =>
      httpClientV1
        .get<{data: TLiveClassApiResponse[]}>(`live-event/draft`)
        .then((response) => response.data),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};
/**
 * Permanent, documented exception to the v1 -> v2 migration: `LiveEventRepositoryV2`
 * has no draft concept at all, so there is no v2 endpoint to save a live class
 * as a draft. Calls v1 directly via `httpClientV1` (same pattern as
 * `MediaRepositoryV1.uploadContent` and `StripeRepository.loginToStripe`)
 * instead of the generic feature-flag-driven `useClient`. Do not migrate this to `useClientV2` — ask
 * before changing.
 */
const useSaveLiveClassAsDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (liveClassData: Partial<TCreateLiveClassApiPayload>) =>
      httpClientV1.post(`draft/live-event`, liveClassData),

    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
      AnalyticsEvent("Live events", "Live class draft fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Live events", "Live class draft success");
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({queryKey: ["live_classes"]}),
        queryClient.invalidateQueries({queryKey: ["live_classes_draft"]}),
      ]),
  });
};
/**
 * Delete a live class — v2 `DELETE live-event/:eventId`, delegated to
 * `features/live-events` (`LiveEventRepositoryV2.delete`).
 *
 * The path is byte-identical to v1, but v2 parses `:eventId` with
 * `ParseIntPipe` and answers `{status, message, statusCode}` with no `data`.
 * Nothing reads the mutation result, so the repository's `Promise<void>` is
 * enough; the string -> number coercion happens here to keep the
 * `mutate(String(eventId))` signature the card menu already passes.
 *
 * Optimistic update, rollback, toasts, analytics events and the
 * `["live_classes"]` invalidation are unchanged.
 */
const useDeleteLiveClass = () => {
  const queryClient = useQueryClient();
  const repository = createLiveEventRepository();
  return useMutation({
    mutationFn: (eventId: string) => repository.delete(Number(eventId)),
    onMutate: (eventId) => {
      const oldEvent = queryClient.getQueryData(["live_classes"]);
      queryClient.setQueryData(
        ["live_classes"],
        (oldValues: LiveEventListItem[] | undefined) => {
          return oldValues?.filter((item) => String(item.id) !== String(eventId));
        }
      );

      return () => queryClient.setQueryData(["live_classes"], oldEvent);
    },
    onError: (error: AxiosError<{message: string}>, values, rollback) => {
      message.error(error.message);
      AnalyticsEvent("Live event", "Live event delete fail");
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSuccess: () => {
      AnalyticsEvent("Live event", "Live event delete success");
    },
    onSettled: () => queryClient.invalidateQueries({queryKey: ["live_classes"]}),
  });
};
export {
  useGetLiveClasses,
  useGetLiveClassesDraft,
  useSaveLiveClassAsDraft,
  useDeleteLiveClass,
};
